import _ from 'lodash'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { SignatureCanvasProps } from 'react-signature-canvas'
import { compose } from 'recompose'
import { Field, InjectedFormProps, change, reduxForm } from 'redux-form'

import { DISPLAY_SHORT_DOT_DELAMINATE_DATE_FORMAT } from 'constants/date'
import { Option } from 'constants/models'
import { STATUS_OPTIONS_VALUES } from 'services/users/constants'

import { isRequired } from 'utils/fieldValidation'

import { Form, Section, Typography } from 'components'
import { Option as DropdownOption } from 'components/InfiniteDropdowns/InfiniteDropdownsModel'
import { SelectedImage } from 'components/PhotoUpload/PhotoUpload'

import i18n from 'translations'

export interface AddAdministrationFormValues {
  administerSignature?: SelectedImage
  administratedBy?: Option
  child?: DropdownOption
  dose?: Option
  medicine?: Option
  otherNotes?: string
  timeOfAdministration?: moment.Moment | number
  witness?: Option
  witnessSignature?: SelectedImage
}

interface AddAdministrationModalFormProps {
  doseOptions?: Option[]
  formValues: AddAdministrationFormValues
  isChildContext: boolean
  isSubmitting: boolean
  medicineOptions?: Option[]
  onCancelClick: () => void
  onSubmit: (fields: AddAdministrationFormValues) => void
}

export const ADD_ADMINISTRATION_FORM = 'AddAdministrationForm'

const mapDispatch = {
  changeValue: (field, value) => change(ADD_ADMINISTRATION_FORM, field, value),
}

const connector = connect(null, mapDispatch)

type PropsFromRedux = ConnectedProps<typeof connector>

type FinalProps = InjectedFormProps<AddAdministrationFormValues, AddAdministrationModalFormProps>
  & AddAdministrationModalFormProps
  & PropsFromRedux

const AddAdministrationModalForm: React.FC<FinalProps> = ({
  changeValue,
  doseOptions,
  formValues,
  handleSubmit,
  isChildContext,
  isSubmitting,
  medicineOptions,
  onCancelClick,
  onSubmit,
}) => {
  const [isInitialized, setIsInitialized] = useState<boolean>(false)

  const { administratedBy, child, medicine, witness } = formValues || {}

  const administerSignatureRef = useRef<SignatureCanvasProps>(null)
  const witnessSignatureRef = useRef<SignatureCanvasProps>(null)

  useEffect(() => {
    setIsInitialized(true)
  }, [])

  useEffect(() => {
    if (isInitialized && administerSignatureRef.current) {
      administerSignatureRef.current.clearSignature()
    }

    if (!administratedBy) {
      changeValue('administerSignature', null)
    }
  }, [administratedBy])

  useEffect(() => {
    if (isInitialized && witnessSignatureRef.current) {
      witnessSignatureRef.current.clearSignature()
    }

    if (!witness) {
      changeValue('witnessSignature', null)
    }
  }, [witness])

  const renderSignature = () => (
    <Section.SubSection title={i18n.t('modals:Administration:AddAdministration:Form:Signatures:title')}>
      <Form.Row
        label={i18n.t('modals:Administration:AddAdministration:Form:Signatures:StaffDropdown:label')}
        width={{ field: '300px' }}
        required
        verticalLabel
      >
        <Field
          component={Form.InfiniteDropdowns.Memberships}
          name="administratedBy"
          placeholder={i18n.t('modals:Administration:AddAdministration:Form:Signatures:StaffDropdown:placeholder')}
          status={STATUS_OPTIONS_VALUES.ACTIVE}
          validate={isRequired}
          searchable
        />
      </Form.Row>
      {administratedBy && (
        <Form.Row
          label={i18n.t('modals:Administration:AddAdministration:Form:Signatures:StaffSignature:label')}
          required
          verticalLabel
        >
          <Field
            component={Form.SignaturePad}
            name="administerSignature"
            signatureRef={administerSignatureRef}
            validate={isRequired}
            searchable
          />
        </Form.Row>
      )}
      <Form.Row
        label={i18n.t('modals:Administration:AddAdministration:Form:Signatures:WitnessDropdown:label')}
        width={{ field: '300px' }}
        required
        verticalLabel
      >
        <Field
          component={Form.InfiniteDropdowns.Memberships}
          name="witness"
          placeholder={i18n.t('modals:Administration:AddAdministration:Form:Signatures:WitnessDropdown:placeholder')}
          status={STATUS_OPTIONS_VALUES.ACTIVE}
          validate={isRequired}
          searchable
        />
      </Form.Row>
      {witness && (
        <Form.Row
          label={i18n.t('modals:Administration:AddAdministration:Form:Signatures:WitnessSignature:label')}
          required
          verticalLabel
        >
          <Field
            component={Form.SignaturePad}
            name="witnessSignature"
            signatureRef={witnessSignatureRef}
            validate={isRequired}
            searchable
          />
        </Form.Row>
      )}
    </Section.SubSection>
  )

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Typography margin="10px 0 15px" variant="h6">
        {moment().format(DISPLAY_SHORT_DOT_DELAMINATE_DATE_FORMAT)}
      </Typography>
      <Section.SubSection title={i18n.t('modals:Administration:AddAdministration:Form:title')}>
        <Form.Row label={_.upperFirst(i18n.t('global:child'))} width={{ field: '300px' }} required verticalLabel>
          <Field
            component={Form.InfiniteDropdowns.RegisterChildren}
            disabled={isChildContext}
            name="child"
            placeholder={i18n.t('modals:Administration:AddAdministration:Form:Child:placeholder')}
            validate={isRequired}
          />
        </Form.Row>
        <Form.Row
          label={i18n.t('modals:Administration:AddAdministration:Form:Medicine:label')}
          width={{ field: '300px' }}
          required
          verticalLabel
        >
          <Field
            component={Form.Select}
            disabled={!child}
            name="medicine"
            options={medicineOptions}
            placeholder={i18n.t('modals:Administration:AddAdministration:Form:Medicine:placeholder')}
            validate={isRequired}
          />
        </Form.Row>
        <Form.Row
          label={i18n.t('modals:Administration:AddAdministration:Form:Dose:label')}
          width={{ field: '300px' }}
          required
          verticalLabel
        >
          <Field
            component={Form.Select}
            disabled={!child || !medicine || 1 >= doseOptions?.length}
            name="dose"
            options={doseOptions}
            placeholder={i18n.t('modals:Administration:AddAdministration:Form:Dose:placeholder')}
            validate={isRequired}
          />
        </Form.Row>
        <Form.Row label={i18n.t('global:Time')} required verticalLabel>
          <Field component={Form.TimePicker} name="timeOfAdministration" validate={isRequired} />
        </Form.Row>
        <Form.Row
          label={i18n.t('modals:Administration:AddAdministration:Form:OtherNotes:label')}
          width={{ field: '100%' }}
          verticalLabel
        >
          <Field component={Form.TextAreaField} name="otherNotes" />
        </Form.Row>
        {renderSignature()}
        <Form.FooterActions
          isSubmitting={isSubmitting}
          submitLabel={i18n.t('global:Save')}
          onCancelClick={onCancelClick}
        />
      </Section.SubSection>
    </Form>
  )
}

const validate = (fields: AddAdministrationFormValues) => {
  const errors = {
    witness: null,
  }

  const { administratedBy, witness } = fields || {}

  if (administratedBy && witness && administratedBy.value === witness.value) {
    errors.witness = i18n.t('modals:Administration:AddAdministration:Form:sameStaffSelectionValidation')
  }

  return errors
}

const enhance = compose(
  reduxForm<AddAdministrationFormValues, AddAdministrationModalFormProps>({ form: ADD_ADMINISTRATION_FORM, validate }),
  connector,
)

export default enhance(AddAdministrationModalForm)
